import { ChangeEvent, useCallback } from 'react';
import { useForm } from 'react-hook-form';

import SimpleModal from 'lib/modal/simple-modal';
import {
  SimpleModalContentStyled,
  SimpleModalVerticalActionsStyled,
} from 'lib/modal/simple-modal.styles';
import { ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE } from 'approot/activity-builder/activity-builder.constants';
import { PStyled } from 'approot/shared/styles/typography.style';
import { TextInputStyled } from 'approot/shared/styles/inputs.style';
import {
  SettingsGroup,
  SettingsHr,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { Field } from './response.constants';
import Switch from 'approot/shared/switch/switch';
import {
  ActivityBuilderDrawerActions,
  ActivityBuilderDrawerContent,
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
} from 'approot/activity-builder/activity-builder-drawer';
import { FlexLeftAlign } from 'approot/shared/styles/flex.style';
import StepInput from 'approot/shared/step-input/step-input';
import ValidationMessage from 'approot/shared/validation/validation-message';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import { NegativeButton } from 'approot/shared/styles/buttons';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { MoreInfo } from '../shared/more-information/more-information-toolip';
import { COLOURS } from './response.constants';
import { ColourTokenRadio } from 'approot/shared/colour/colour-token';
import { SecondaryButton } from 'approot/shared/styles/buttons/secondary-button.style';
import { Box } from 'approot/shared/design-system/box/box.style';

const PLACEHOLDER_FIELD = 'placeholderField';
const RESPONSE_SIZE_FIELD = 'responseSize';
const RESPONSE_SIZE_MAX = 12;
const RESPONSE_SIZE_MIN = 1;

type FormData = {
  [RESPONSE_SIZE_FIELD]: number;
};

type Props = {
  field: Field;
  index: number;
  canDelete: boolean;
  handleOnChange: (updatedField: Field) => void;
  handleDelete: (id: string) => void;
  goBack: () => void;
};

const ResponseSettingsField = ({
  field,
  index,
  canDelete,
  handleOnChange,
  handleDelete,
  goBack,
}: Props) => {
  const { isVisible, openModal, closeModal } = useSimpleModalState();

  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      [RESPONSE_SIZE_FIELD]: field.numberOfLines || 1,
    },
  });

  const {
    formState: { errors },
    register,
    watch,
    setValue,
    clearErrors,
  } = formMethods;
  const responseSize = watch(RESPONSE_SIZE_FIELD);

  const { onChange: registerOnChange } = register(RESPONSE_SIZE_FIELD);

  const updateResponseSize = useCallback(
    (value: number) => {
      setValue(RESPONSE_SIZE_FIELD, value);
      const isValueInRange =
        value <= RESPONSE_SIZE_MAX && value >= RESPONSE_SIZE_MIN;
      if (isValueInRange) {
        clearErrors(RESPONSE_SIZE_FIELD);
        if (value !== field.numberOfLines) {
          handleOnChange({ ...field, numberOfLines: value });
        }
      }
    },
    [field, handleOnChange, setValue, clearErrors]
  );

  const handleResponseSizeOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      e.target.value && updateResponseSize(Number(e.target.value));
      registerOnChange(e);
    },
    [registerOnChange, updateResponseSize]
  );

  const handleIncrement = useCallback(() => {
    const newTotal: number = Number(responseSize) + 1;
    updateResponseSize(newTotal);
  }, [responseSize, updateResponseSize]);

  const handleDecrement = useCallback(() => {
    const newTotal: number = Number(responseSize) - 1;
    updateResponseSize(newTotal);
  }, [responseSize, updateResponseSize]);

  return (
    <ActivityBuilderDrawerWrapper>
      {isVisible && (
        <SimpleModal
          isVisible
          title="Remove Response"
          headerTitle="Remove Response"
          openBodyClassName={ACTIVITY_BUILDER_CLICK_CLEAR_IGNORE}
          onClose={closeModal}
        >
          <SimpleModalContentStyled>
            <PStyled marginBottom={0}>
              Are you sure you want to remove this Response?
            </PStyled>
            <SimpleModalVerticalActionsStyled>
              <NegativeButton
                type="button"
                data-selector="confirm-button"
                onClick={() => {
                  handleDelete(field.itemId);
                  goBack();
                }}
              >
                Remove Response
              </NegativeButton>
            </SimpleModalVerticalActionsStyled>
          </SimpleModalContentStyled>
        </SimpleModal>
      )}
      <ActivityBuilderDrawerHeader
        title={`Response ${index + 1}`}
        isBack
        onClose={goBack}
      />
      <ActivityBuilderDrawerContent>
        <SettingsGroup as="fieldset" marginBottom={0}>
          <ScreenReaderOnly as="legend">Text Response</ScreenReaderOnly>
          <SettingsGroup>
            <SettingsLabel htmlFor={RESPONSE_SIZE_FIELD}>
              Number of Lines for Print
            </SettingsLabel>
            <FlexLeftAlign>
              <StepInput
                data-selector="response-size"
                id={RESPONSE_SIZE_FIELD}
                {...register(RESPONSE_SIZE_FIELD, {
                  max: {
                    value: RESPONSE_SIZE_MAX,
                    message: `There can only be a maximum of ${RESPONSE_SIZE_MAX} lines`,
                  },
                  min: {
                    value: RESPONSE_SIZE_MIN,
                    message: 'There must be at least 1 line',
                  },
                })}
                onIncrementClick={handleIncrement}
                onDecrementClick={handleDecrement}
                isDecrementDisabled={responseSize <= RESPONSE_SIZE_MIN}
                isIncrementDisabled={responseSize >= RESPONSE_SIZE_MAX}
                aria-required
                aria-invalid={!!errors[RESPONSE_SIZE_FIELD]?.message}
                aria-describedby={`${RESPONSE_SIZE_FIELD}-error`}
                onChange={handleResponseSizeOnChange}
                $hasError={!!errors[RESPONSE_SIZE_FIELD]?.message}
              />
            </FlexLeftAlign>
            <ValidationMessage
              id={`${RESPONSE_SIZE_FIELD}-error`}
              field={RESPONSE_SIZE_FIELD}
              errors={errors}
            />
          </SettingsGroup>
          <SettingsHr />
          <SettingsGroup>
            <SettingsLabel htmlFor={PLACEHOLDER_FIELD}>
              Placeholder Text
            </SettingsLabel>
            <TextInputStyled
              data-selector="txt-add-answer"
              id={PLACEHOLDER_FIELD}
              name={PLACEHOLDER_FIELD}
              defaultValue={field.placeholder || ''}
              placeholder="Type here..."
              onChange={event =>
                handleOnChange({
                  ...field,
                  placeholder: event.target.value,
                })
              }
            />
          </SettingsGroup>

          <SettingsGroup>
            <SettingsLabel>
              Scaffold Placeholder Text
              <MoreInfo>
                Turning this on will allow students to continue their response
                with your placeholder text
              </MoreInfo>
            </SettingsLabel>
            <Switch
              ariaLabel={`${
                field.isPlaceholderScaffold ? 'Hide' : 'Show'
              } placeholder as scaffold`}
              state={!!field.isPlaceholderScaffold}
              onClick={() => {
                handleOnChange({
                  ...field,
                  isPlaceholderScaffold: !!!field.isPlaceholderScaffold,
                });
              }}
            />
          </SettingsGroup>

          <SettingsHr />

          <SettingsGroup>
            <SettingsLabel>Background colour</SettingsLabel>
            <Box $gap={8} $alignItems="center">
              <div role="radiogroup">
                {COLOURS.map(item => (
                  <ColourTokenRadio
                    key={item.colour1}
                    name="backgroundColourField"
                    colours={item}
                    isActive={item.base === field.baseColour}
                    onChange={() =>
                      handleOnChange({
                        ...field,
                        baseColour: item.base,
                      })
                    }
                  />
                ))}
              </div>
              {field.baseColour && (
                <SecondaryButton
                  type="button"
                  $isSmall
                  onClick={() =>
                    handleOnChange({
                      ...field,
                      baseColour: null,
                    })
                  }
                >
                  Clear
                </SecondaryButton>
              )}
            </Box>
          </SettingsGroup>
        </SettingsGroup>
      </ActivityBuilderDrawerContent>
      {canDelete && (
        <ActivityBuilderDrawerActions>
          <NegativeButton
            type="button"
            variant="outlined"
            onClick={openModal}
            data-selector="btn-delete-response"
          >
            Delete Response
          </NegativeButton>
        </ActivityBuilderDrawerActions>
      )}
    </ActivityBuilderDrawerWrapper>
  );
};

export default ResponseSettingsField;
